import { Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useCrosspostingStatus } from '~/entities/crossposting/model/use-crossposting-status'
import { useListing } from '~/entities/listing/model/use-listings'
import { useProfileScope } from '~/entities/profile/model/use-profiles'
import { CrosspostingUnavailable } from '~/features/crossposting/components/crossposting-unavailable'
import { CROSSPOSTING_TOOL_META, type CrosspostingToolKey } from '~/features/crossposting/lib/tools'
import * as m from '~/paraglide/messages'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { FeatureCard } from '~/shared/ui/feature-card'
import { Skeleton } from '~/shared/ui/skeleton'
import { PageHeader } from '~/widgets/page-header'

export function CrosspostingPlaceTools({ listingId }: { listingId: string }) {
  const navigate = useNavigate()
  const { data: business, isLoading: isBusinessLoading } = useCurrentBusiness()
  const { data: listing, isLoading: isListingLoading } = useListing(business?.id ?? '', listingId)
  const { data: status } = useCrosspostingStatus(business?.id ?? '')
  const { enabled: profilesEnabled } = useProfileScope()

  const tools: { key: CrosspostingToolKey; onClick: () => void }[] = [
    {
      key: 'connect',
      onClick: () =>
        navigate({ to: '/dashboard/crossposting/$listingId/connect', params: { listingId } }),
    },
    {
      key: 'facebook',
      onClick: () =>
        navigate({ to: '/dashboard/crossposting/$listingId/facebook', params: { listingId } }),
    },
    {
      key: 'instagram',
      onClick: () =>
        navigate({ to: '/dashboard/crossposting/$listingId/instagram', params: { listingId } }),
    },
    {
      key: 'cross-post',
      onClick: () =>
        navigate({ to: '/dashboard/crossposting/$listingId/cross-post', params: { listingId } }),
    },
    {
      key: 'overview',
      onClick: () =>
        navigate({ to: '/dashboard/crossposting/$listingId/overview', params: { listingId } }),
    },
  ]

  const backLink = profilesEnabled ? null : (
    <Button asChild variant="ghost" size="sm" className="-ml-2 w-fit">
      <Link to="/dashboard/crossposting">
        <ArrowLeft className="h-4 w-4" />
        {m.crosspostingBackToPlaces()}
      </Link>
    </Button>
  )

  if (isBusinessLoading || (business && isListingLoading)) {
    return (
      <div className="space-y-6">
        {backLink}
        <Skeleton className="h-9 w-64" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {CROSSPOSTING_TOOL_KEYS.map((key) => (
            <Skeleton key={key} className="h-40 w-full rounded-xl" />
          ))}
        </div>
      </div>
    )
  }

  if (status && !status.enabled) {
    return (
      <div className="space-y-6">
        {backLink}
        <CrosspostingUnavailable />
      </div>
    )
  }

  if (!listing) {
    return (
      <div className="space-y-6">
        {backLink}
        <Card className="flex flex-col items-start gap-3 p-6">
          <p className="font-medium">{m.listingNotFound()}</p>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {backLink}
      <PageHeader title={listing.name} description={m.crosspostingToolsDescription()} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {tools.map((tool) => {
          const meta = CROSSPOSTING_TOOL_META[tool.key]
          return (
            <FeatureCard
              key={tool.key}
              icon={meta.icon}
              title={meta.title()}
              description={meta.description()}
              onClick={tool.onClick}
            />
          )
        })}
      </div>
    </div>
  )
}

const CROSSPOSTING_TOOL_KEYS = Object.keys(CROSSPOSTING_TOOL_META) as CrosspostingToolKey[]
